import React, { useState, useRef, useEffect, useCallback } from "react";
import { z } from "zod";
import { useFormContext } from "react-hook-form";
import { StepProps } from "@/components/multi-step-form";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Sparkles } from "lucide-react";
import { CustomAlert } from "@/components/custom-alert";
import {
  PhotoUploadGrid,
  PhotoData as BasePhotoData,
} from "@/components/photo-upload-grid";

interface PhotoData extends BasePhotoData {
  url?: string;
  uploaded?: boolean;
  isFromBackend?: boolean;
}
import { UploadErrorModal, UploadError } from "@/components/upload-error-modal";
import { usePhotoUploadValidation } from "@/hooks/use-photo-upload-validation";
import { sendTrackDataServer } from "@/features/analytics/utils/serverTrackData";
import { validateAuthentication } from "@/utils/auth-middleware";
import { useLocation } from "react-router-dom";
import { useAuth } from "@/features/authentication";
import { useUploadState } from "@/contexts/upload-state-context";

export const midiasEditSchema = z.object({
  photos: z
    .array(z.any(), { required_error: 'O campo "Fotos" é obrigatório' })
    .min(
      2,
      "Adicione entre 2 e 5 fotos para que clientes conheçam sua sala antes da visita",
    )
    .max(5, "Máximo de 5 fotos permitidas")
    .refine(
      (photos) =>
        photos.every((photo: any) => {
          return photo.tags && photo.tags.length > 0;
        }),
      "Cada foto deve ter pelo menos uma tag",
    ),
  videoUrls: z
    .array(z.string())
    .optional()
    .transform((urls) => urls?.filter((url) => url && url.trim() !== "") || [])
    .refine((urls) => {
      if (!urls || urls.length === 0) return true;

      return urls.every((url) => {
        try {
          const parsedUrl = new URL(url);
          return (
            parsedUrl.hostname.includes(".") && parsedUrl.hostname.length > 3
          );
        } catch {
          return false;
        }
      });
    }, "URL inválida. Insira uma URL válida com domínio (ex: https://zoom.us/...)")
    .refine((urls) => !urls || urls.length <= 1, "Máximo de 1 vídeo permitido"),
});

export type MidiasEditData = z.infer<typeof midiasEditSchema>;

// Global validation function that will be used by multi-step-form
let globalValidatePhotosEdit:
  | ((
      photos: any[],
      tagsAlreadyValidated?: boolean,
    ) => Promise<{ valid: boolean; errors?: Record<string, string[]> }>)
  | null = null;

export const StepMidiasEdit: React.FC<StepProps> = ({
  errors,
  registerField,
}) => {
  const { watch, setValue, register } = useFormContext();
  const { pathname } = useLocation();
  const { userInfo } = useAuth();
  const watchedValues = watch();
  const { uploadPhotos, isUploading, uploadProgress } =
    usePhotoUploadValidation();
  const { setUploadState, resetUploadState } = useUploadState();

  const [backendPhotosLoaded, setBackendPhotosLoaded] = React.useState(false);

  const [errorModal, setErrorModal] = useState<{
    isOpen: boolean;
    errors: UploadError[];
  }>({
    isOpen: false,
    errors: [],
  });

  // Store resetUploadState in a ref so it can be used in the validation closure
  const resetUploadStateRef = useRef(resetUploadState);
  resetUploadStateRef.current = resetUploadState;

  // Update context upload state when local state changes
  useEffect(() => {
    setUploadState({
      isUploading: isUploading,
      uploadingText: uploadProgress.uploadingText,
    });
  }, [isUploading, uploadProgress.uploadingText, setUploadState]);

  // Initialize backend photos in the form if they exist and haven't been loaded yet
  React.useEffect(() => {
    if (
      !backendPhotosLoaded &&
      watchedValues.photos &&
      watchedValues.photos.length > 0
    ) {
      // Check if photos have the backend structure (url + tags)
      const hasBackendPhotos = watchedValues.photos.some(
        (photo: any) => photo.url && !photo.file && photo.tags,
      );

      if (hasBackendPhotos) {
        console.log(
          "Loading backend photos into PhotoUploadGrid:",
          watchedValues.photos,
        );
        // Transform backend photos to PhotoData format
        const transformedPhotos = watchedValues.photos.map(
          (photo: any, index: number) => ({
            id: `backend-${index}`,
            file: null,
            preview: photo.url,
            url: photo.url,
            tags: photo.tags || [],
            uploaded: true,
            isFromBackend: true,
          }),
        );

        setValue("photos", transformedPhotos);
        setBackendPhotosLoaded(true);
      }
    }
  }, [watchedValues.photos, backendPhotosLoaded, setValue]);

  React.useEffect(() => {
    globalValidatePhotosEdit = async (
      photos: any[],
      tagsAlreadyValidated = false,
    ) => {
      if (!photos || photos.length === 0) {
        return {
          valid: false,
          errors: { photos: ["Adicione pelo menos 2 fotos."] },
        };
      }

      if (photos.length < 2) {
        return {
          valid: false,
          errors: { photos: ["Adicione pelo menos 2 fotos para continuar."] },
        };
      }

      if (!tagsAlreadyValidated) {
        const photosWithoutTags: number[] = [];
        photos.forEach((photo: any, index: number) => {
          if (!photo.tags || photo.tags.length === 0) {
            photosWithoutTags.push(index + 1);
          }
        });

        if (photosWithoutTags.length > 0) {
          const errorMessage =
            photosWithoutTags.length === 1
              ? `Foto ${photosWithoutTags[0]}: adicione pelo menos uma tag.`
              : `Fotos ${photosWithoutTags.join(", ")}: adicionem pelo menos uma tag cada.`;

          return {
            valid: false,
            errors: { photos: [errorMessage] },
          };
        }
      }

      const allPhotosUploaded = photos.every(
        (photo: any) =>
          photo.url &&
          photo.url.trim() !== "" &&
          !photo.url.includes("undefined"),
      );

      if (!allPhotosUploaded) {
        return {
          valid: false,
          errors: {
            photos: [
              "Algumas fotos ainda não foram enviadas. Aguarde o upload concluir.",
            ],
          },
        };
      }

      return { valid: true };
    };
  }, []);

  const handlePhotosChange = useCallback(
    async (photos: PhotoData[]) => {
      const newPhotos = photos.filter(
        (photo) =>
          !photo.url ||
          photo.url.trim() === "" ||
          photo.url.includes("undefined"),
      );

      if (newPhotos.length > 0) {
        setValue("photos", photos);

        try {
          const isExternalRoute = pathname.includes("/external/");
          const authResult = validateAuthentication(isExternalRoute);

          sendTrackDataServer({
            eventName: "PRIVATE_ROOM_PHOTO_UPLOAD_STARTED",
            data: {
              user_id:
                userInfo?.user?.id ||
                authResult.payload?.id ||
                authResult.payload?.userId ||
                "unknown",
              email:
                userInfo?.profile?.email ||
                authResult.payload?.email ||
                "unknown",
              flow_type: "edit" as const,
            },
            url: pathname,
            productType: "PO",
          });

          const uploadResult = await uploadPhotos(newPhotos);

          if (uploadResult.success) {
            let uploadIndex = 0;
            const updatedPhotos = photos.map((photo) => {
              if (
                photo.url &&
                photo.url.trim() !== "" &&
                !photo.url.includes("undefined")
              ) {
                return { ...photo, uploaded: true };
              }

              const newUrl =
                uploadResult.uploadedUrls[uploadIndex] || photo.url;
              uploadIndex++;

              return {
                ...photo,
                url: newUrl,
                uploaded: true,
              };
            });

            setValue("photos", updatedPhotos);

            sendTrackDataServer({
              eventName: "PRIVATE_ROOM_PHOTO_UPLOAD_SUCCESS",
              data: {
                user_id:
                  userInfo?.user?.id ||
                  authResult.payload?.id ||
                  authResult.payload?.userId ||
                  "unknown",
                email:
                  userInfo?.profile?.email ||
                  authResult.payload?.email ||
                  "unknown",
                flow_type: "edit" as const,
              },
              url: pathname,
              productType: "PO",
            });
          } else if (!uploadResult.success) {
            const originalIndexes: number[] = [];
            photos.forEach((photo, index) => {
              if (
                !photo.url ||
                photo.url.trim() === "" ||
                photo.url.includes("undefined")
              ) {
                originalIndexes.push(index);
              }
            });

            setErrorModal({
              isOpen: true,
              errors:
                uploadResult.errors?.map((err, idx) => ({
                  photoIndex: originalIndexes[idx] ?? idx,
                  error: err.error,
                  photoPreview: err.photoPreview,
                  fileName: err.fileName,
                })) || [],
            });

            sendTrackDataServer({
              eventName: "PRIVATE_ROOM_PHOTO_UPLOAD_ERROR",
              data: {
                user_id:
                  userInfo?.user?.id ||
                  authResult.payload?.id ||
                  authResult.payload?.userId ||
                  "unknown",
                email:
                  userInfo?.profile?.email ||
                  authResult.payload?.email ||
                  "unknown",
                flow_type: "edit" as const,
                error_message: uploadResult.errors
                  .map((err) => err.error)
                  .join(", "),
              },
              url: pathname,
              productType: "PO",
            });
          }
        } catch (error) {
          console.error("Error uploading photos:", error);
        }
      } else {
        setValue("photos", photos);
      }
    },
    [
      watchedValues.photos,
      setValue,
      uploadPhotos,
      userInfo,
      pathname,
      setErrorModal,
    ],
  );

  const handleRemovePhoto = (photoIndex: number) => {
    const currentPhotos = watchedValues.photos || [];
    const updatedPhotos = currentPhotos.filter(
      (_: any, index: number) => index !== photoIndex,
    );
    setValue("photos", updatedPhotos);

    const updatedErrors = errorModal.errors
      .filter((err) => err.photoIndex !== photoIndex)
      .map((err) => ({
        ...err,
        photoIndex:
          err.photoIndex > photoIndex ? err.photoIndex - 1 : err.photoIndex,
      }));

    if (updatedErrors.length === 0 || updatedPhotos.length < 2) {
      setErrorModal({ isOpen: false, errors: [] });
    } else {
      setErrorModal((prev) => ({
        ...prev,
        errors: updatedErrors,
      }));
    }
  };

  return (
    <div className="space-y-8">
      <div
        className={`bg-white rounded-lg border p-6 transition-opacity ${isUploading ? "opacity-75" : ""}`}
      >
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-2xl font-semibold text-gray-900">
            Fotos <span className="text-red-500">*</span>
          </h2>
          {isUploading && (
            <div className="flex items-center gap-2 text-blue-600">
              <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
              <span className="text-sm font-medium">
                {uploadProgress.uploadingText}
              </span>
            </div>
          )}
        </div>
        <p className="text-gray-600 mb-4">
          Adicione entre 2 e 5 fotos para que clientes conheçam sua sala antes
          da visita. Cada foto deve ter pelo menos uma tag.
        </p>

        <div className="mb-6 p-3 bg-amber-50 border border-amber-200 rounded-md">
          <div className="flex items-center gap-2 text-sm">
            <span className="text-amber-800 font-medium">⚠️ Importante:</span>
            <span className="text-amber-700">
              Cada foto precisa de uma tag pra mostrar o que aparece nela.
            </span>
          </div>
        </div>

        {isUploading && (
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
            <div className="flex items-center justify-between text-sm">
              <span className="text-blue-800">
                {uploadProgress.currentPhoto > 0 &&
                  `Processando foto ${uploadProgress.currentPhoto} de ${uploadProgress.totalPhotos}`}
              </span>
              {uploadProgress.totalPhotos > 0 && (
                <span className="text-blue-600 font-medium">
                  {Math.round(
                    (uploadProgress.currentPhoto / uploadProgress.totalPhotos) *
                      100,
                  )}
                  %
                </span>
              )}
            </div>
            {uploadProgress.totalPhotos > 0 && (
              <div className="mt-2 w-full bg-blue-200 rounded-full h-2">
                <div
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                  style={{
                    width: `${(uploadProgress.currentPhoto / uploadProgress.totalPhotos) * 100}%`,
                  }}
                ></div>
              </div>
            )}
          </div>
        )}

        {/* Photo Upload Grid */}
        <div
          ref={
            errors.photos && registerField
              ? (el) => registerField("photos", el)
              : undefined
          }
        >
          <PhotoUploadGrid
            photos={watchedValues.photos || []}
            onPhotosChange={handlePhotosChange}
            maxPhotos={5}
            availableTags={[
              "Sala privativa",
              "Copa da sala",
              "Banheiro privativo",
              "Sala de reunião da sala",
              "Armários da sala",
              "TV",
              "Projetor",
            ]}
          />
        </div>

        {/* Quality Tips */}
        <CustomAlert
          variant="info"
          icon={<Sparkles className="w-5 h-5" />}
          className="mt-6"
        >
          <div>
            <h4 className="font-medium text-sm mb-2">
              Fotos de qualidade aumentam em 90% as chances de conversão:
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
              <ul className="space-y-1">
                <li>• Prefira luz natural, fotografe de dia</li>
                <li>• Mostre ângulos amplos do espaço</li>
                <li>• Organize o ambiente antes da foto</li>
              </ul>
              <ul className="space-y-1">
                <li>• Varie os pontos de vista (entrada, janelas, cantos)</li>
                <li>• Use boa composição para destacar tamanho e uso</li>
              </ul>
            </div>
          </div>
        </CustomAlert>

        {errors.photos && (
          <p className="text-sm text-red-600 mt-2">{errors.photos[0]}</p>
        )}
      </div>

      {/* Video Section */}
      <div className="bg-white rounded-lg border p-6 transition-opacity">
        <h2 className="text-2xl font-semibold text-gray-900 mb-2">Vídeo</h2>
        <p className="text-gray-600 mb-6">
          Você pode adicionar 1 vídeo para complementar a apresentação da sala.
        </p>

        <div
          ref={
            errors.videoUrls && registerField
              ? (el) => registerField("videoUrls", el)
              : undefined
          }
        >
          <Label
            htmlFor="videoUrls"
            className="text-sm font-medium text-gray-700"
          >
            Cole a URL do vídeo (YouTube, Vimeo, Zoom, etc.)
          </Label>
          <Input
            id="videoUrls"
            type="url"
            placeholder="https://zoom.us/rec/..."
            className="mt-1"
            {...register("videoUrls.0")}
          />
        </div>

        {errors.videoUrls && (
          <p className="text-sm text-red-600 mt-2">{errors.videoUrls[0]}</p>
        )}
      </div>

      {/* Upload Error Modal */}
      <UploadErrorModal
        isOpen={errorModal.isOpen}
        onClose={() => setErrorModal({ isOpen: false, errors: [] })}
        errors={errorModal.errors}
        onRemovePhoto={handleRemovePhoto}
        isRetrying={isUploading}
      />
    </div>
  );
};

// Export validation function for use in step definition
export const validateMidiasEditStep = async (formData: any) => {
  // First run the Zod schema validation for both photos and video URLs
  const result = midiasEditSchema.safeParse(formData);

  if (!result.success) {
    const errors: Record<string, string[]> = {};
    result.error.issues.forEach((issue) => {
      const path = issue.path.join(".");
      if (!errors[path]) {
        errors[path] = [];
      }
      errors[path].push(issue.message);
    });

    return {
      valid: false,
      errors,
    };
  }

  // Then run the photo upload validation if it exists
  // Since Zod validation passed, tags are already validated
  if (globalValidatePhotosEdit) {
    return await globalValidatePhotosEdit(formData.photos, true);
  }

  return { valid: true };
};
